import { Temporal } from '../temporal';
import { EMPTY_VALUE } from './constants';

const DEFAULT_LOCALE = 'cs-CZ';

/** Long format: "19. listopadu 2025" */
export function formatLongDate(
  date: Temporal.PlainDate | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (date == null) return EMPTY_VALUE;
  return date.toLocaleString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

/** Short format: "19. 11. 2025" */
export function formatShortDate(
  date: Temporal.PlainDate | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (date == null) return EMPTY_VALUE;
  return date.toLocaleString(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });
}

/** With day of week: "úterý 19. listopadu 2025" */
export function formatDateWithDay(
  date: Temporal.PlainDate | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (date == null) return EMPTY_VALUE;
  return date.toLocaleString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

/** ISO 8601 date: "2025-11-19" */
export function formatDateISO(date: Temporal.PlainDate | null | undefined): string {
  if (date == null) return EMPTY_VALUE;
  return date.toString();
}

/** Date range: "19. listopadu 2025 – 30. listopadu 2025" */
export function formatDateRange(
  from: Temporal.PlainDate | null | undefined,
  to: Temporal.PlainDate | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (from == null || to == null) return EMPTY_VALUE;
  return `${formatLongDate(from, locale)} – ${formatLongDate(to, locale)}`;
}

/** Dot format for compact display: "19.11.2025" */
export function formatDotDate(date: Temporal.PlainDate | null | undefined): string {
  if (date == null) return EMPTY_VALUE;
  const d = String(date.day).padStart(2, '0');
  const m = String(date.month).padStart(2, '0');
  return `${d}.${m}.${date.year}`;
}

/**
 * Format a billing-cycle period string ("YYYY-MM") to a short "Mon YYYY" label.
 *
 * Example: `"2026-05"` → `"May 2026"` (locale-dependent).
 *
 * @param period YYYY-MM string (returned by the billing-window utilities)
 * @param locale BCP 47 locale tag, e.g. `"cs"`, `"en"`. Defaults to `"cs-CZ"`.
 */
export function formatBillingPeriod(period: string, locale: string = DEFAULT_LOCALE): string {
  const [year, month] = period.split('-');
  return Temporal.PlainDate.from({
    year: Number(year),
    month: Number(month),
    day: 1
  }).toLocaleString(locale, { month: 'short', year: 'numeric' });
}
